import React from 'react';
import { Navigate } from 'react-router-dom';

import { AutoComplete, Select } from 'antd';
import Input from 'antd/es/input/Input';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { List } from '@common/typescript/objects/List';

import { rest } from '@app/components/Api';
import { numberBeautifyValidator, validateRequiredString } from '@app/components/LocalCommon/Utils/Validators';
import { eventToLink } from '@app/components/LocalCommon/Utils/eventToLink';
import HelmetProvider from '@app/components/UI/HelmetProvider/HelmetProvider';
import '@app/scss/components/qualityOfLifeAssessment.scss';

const { Option } = Select;

const DecoratedSearch = ({
	formikBag,
	fieldName,
	type = 'text',
	widthResponse = false,
	handleOnChange = (e, formikBag: any, fieldName: string) => {
		formikBag.setFieldValue(fieldName, e.target.value);
	},
	handleOnKeyPress = (e) => {},
}) => {
	return (
		<div className={`decorated-search highlight ${widthResponse ? 'highlight__width-response' : ''}`}>
			<Input
				onChange={(e) => handleOnChange(e, formikBag, fieldName)}
				onKeyPress={handleOnKeyPress}
				type={type}
				value={formikBag.values.fieldName}
				hidden={widthResponse}
			/>
			{widthResponse && (
				<div
					className="decorated-search-mask-input"
					contentEditable
					onKeyUp={(e) => {
						const divText = e.target as HTMLElement;
						formikBag.setFieldValue(fieldName, divText.innerText);
					}}
				/>
			)}
		</div>
	);
};

const QolScale = '/how-will-i-know-it-is-time/Lap-of-Love-Quality-of-Life-Scale.pdf';

const DecoratedSelect = ({
	optionList,
	formikBag,
	fieldName,
	fieldValue,
	onChange = (value) => {},
	disabled = false,
	labelInValue = false,
	classCustom = '',
}) => {
	return (
		<div className={`decorated-select highlight ${classCustom}`}>
			<Select
				onChange={(value) => {
					formikBag.setFieldValue(fieldName, value);
					onChange(value);
				}}
				value={fieldValue}
				classNames={{ popup: { root: 'decorated-select-dropdown' } }}
				popupMatchSelectWidth={false}
				disabled={disabled}
				labelInValue={labelInValue}
			>
				{optionList.map((item, index) => (
					<Option key={item.name + index} value={item.id ? item.id : item.value}>
						{item.name}
					</Option>
				))}
			</Select>
		</div>
	);
};

const DecoratedAutoComplete: React.FC<{
	optionList: Array<Disease>;
	formikBag: FormikProps<PetInfo>;
	fieldName: string;
	fieldValue: number| null;
	disabled: boolean;
}> = ({
	optionList,
	formikBag,
	fieldName,
	fieldValue,
	disabled = false,
}) => {
	const [name, setName] = React.useState<string>('');

	const options: Array<{ label: string, value: string }> = optionList.map((item) => {
		return { label: item.name, value: item.name };
	});

	React.useEffect(() => {
		setName(fieldValue === 0 ? 'a disease' : (optionList.find((option) => option.id === fieldValue)?.name || ''));
	}, [fieldValue]);

	return (
		<div className="decorated-autoComplete highlight">
			<AutoComplete
				classNames={{ popup: { root: 'decorated-select-dropdown' } }}
				popupMatchSelectWidth={false}
				onSearch={(value) => {
					setName(value);
				}}
				onSelect={(value, option) => {
					formikBag.setFieldValue(fieldName, value);
					setName(option.label);
				}}
				onBlur={(e) => {
					const result = optionList.find((option) => option.name === name);
					formikBag.setFieldValue(fieldName, result?.id || null);
					setName(result?.name || '');
				}}
				options={options}
				value={name}
				disabled={disabled}
				filterOption={(inputValue, option) => typeof option?.label === 'string' && option?.label?.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1}
			>
				<input type="text" size={name?.length || 3} />
			</AutoComplete>
		</div>
	);
};

interface Disease {
	name: string;
	deleted: boolean;
	id: number;
}

export enum DotLocation {
	Head = 1,
	HeadFront = 2,
	Body = 3,
	Legs = 4,
	BackLegs = 5,
	Belly = 6,
	Back = 7,
}

export const tooltipDotLocation = {
	1: 'Mental Health',
	2: 'Natural Functions',
	3: 'Physical Health',
	4: 'Physical Health',
	5: 'Natural Functions',
	6: 'Natural Functions',
	7: 'Natural Functions',
};

export interface Question {
	text: string;
	category: string;
	dotLocation: DotLocation;
}

interface PetInfo {
	years: number | null;
	gender?: number | null;
	weight?: number | null;
	petType: string;
	name: string;
	hasDiagnosis: boolean | null;
	disease: number | null;
}

export interface Session {
	questions: Array<Question>;
	sessionId: string;
	petType: 'dog' | 'cat' | 'other';
	petName: string;
}

const validationSchema = Yup.object({
	years: numberBeautifyValidator('Years is a required field'),
	petType: validateRequiredString('Pet type is a required field'),
	name: validateRequiredString('Name is a required field'),
	hasDiagnosis: Yup.boolean().required('Has diagnosis field must be completed.'),
	gender: Yup.number().min(1).max(2).required('Gender field must be completed'),
	weight: numberBeautifyValidator('Weight is a required field'),
	disease: Yup.number()
		.nullable(true)
		.when('hasDiagnosis', {
			is: true,
			then: Yup
				.number()
				.integer()
				.min(-1)
				.nullable(false)
				.required('Disease field must be completed'),
		}),
});

const initialValues: PetInfo = {
	years: null,
	gender: null,
	weight: null,
	petType: '',
	name: '',
	hasDiagnosis: null,
	disease: null,
};

const QualityOfLifeAssessment: React.FC = () => {
	const [diseaseList, setDiseaseList] = React.useState<Array<Disease>>([]);
	const [questions, setQuestions] = React.useState<Array<Question>>([]);
	const [petType, setPetType] = React.useState<string>('');
	const [pet, setPet] = React.useState<PetInfo>({
		years: null,
		petType: '',
		name: '',
		weight: null,
		gender: null,
		hasDiagnosis: null,
		disease: null,
	});
	const [sessionId, setSessionId] = React.useState('');
	React.useEffect(() => {
		rest.get<List<Disease>>(`v1/disease/${petType}`).then((res) => {
			setDiseaseList([...res.list, { name: 'Disease not listed', id: -1, deleted: false }]);
		});
	}, [petType]);

	const handleOnChange = (e, formikBag, fieldName) => {
		const input = e.target as HTMLInputElement;
		formikBag.setFieldValue(fieldName, parseInt(input.value, 10));
		input.value = parseInt(input.value, 10).toString();
	};

	const handleOnKeyPress = (e) => {
		if (!(e.charCode >= 48 && e.charCode <= 57)) {
			e.preventDefault();
			return false;
		}
	};

	return (
		<HelmetProvider>
			<div className="pet-data-form__wrapper">
				<div className="pet-data-form">
					<h1>Quality-of-Life Assessment</h1>
					<h6>
						Curious or concerned about your pet's quality of life? It only takes a few questions for us to provide you with more information.
					</h6>
					<Formik<PetInfo>
						onSubmit={(values, formikActions) => {
							!values.disease && formikActions.setFieldValue('disease', 0);
							!values.disease ? setPet({ ...values, disease: 0 }) : setPet(values);
							values.petType !== 'other'
								? rest.post<Session>('v1/qol', !values.disease ? { ...values, disease: 0 } : values)
									.then((res) => {
										setSessionId(res.sessionId);
										setQuestions(res.questions);
									})
								: setQuestions([
									{
										text: '',
										dotLocation: DotLocation.Head,
										category: '',
									},
								]);
						}}
						initialValues={initialValues}
						validationSchema={validationSchema}
					>
						{(formikBag: FormikProps<PetInfo>) => {
							return !questions.length ? (
								<Form>
									<span className="pet-data-form__row">
										I have a
										<DecoratedSearch
											type="number"
											formikBag={formikBag}
											fieldName="years"
											handleOnChange={handleOnChange}
											handleOnKeyPress={handleOnKeyPress}
										/>
										year&nbsp;
										<br className="xs-break" />
										old,&nbsp;
										<DecoratedSelect
											optionList={[
												{ name: 'male', value: 1 },
												{ name: 'female', value: 2 },
											]}
											formikBag={formikBag}
											fieldName="gender"
											fieldValue={formikBag.values.gender}
											onChange={(value) => {
												formikBag.setFieldTouched('gender', true);
											}}
										/>
										&nbsp;
										<br className="sm-break" />
										<DecoratedSelect
											optionList={[
												{ name: 'dog', value: 'dog' },
												{ name: 'cat', value: 'cat' },
												{ name: 'other', value: 'other' },
											]}
											formikBag={formikBag}
											fieldName="petType"
											fieldValue={formikBag.values.petType}
											onChange={(value) => {
												formikBag.setFieldTouched('petType', true);
												setPetType(value);
											}}
										/>
										<br className="xs-break" />
										named
										{' '}
										<DecoratedSearch formikBag={formikBag} fieldName="name" widthResponse />
										,
									</span>
									<br />
									<span className="pet-data-form__row">
										who weighs
										<DecoratedSearch
											type="number"
											formikBag={formikBag}
											fieldName="weight"
											handleOnChange={handleOnChange}
											handleOnKeyPress={handleOnKeyPress}
										/>
										lbs and
										<DecoratedSelect
											optionList={[
												{ name: 'has', value: true },
												{ name: 'has not', value: false },
											]}
											formikBag={formikBag}
											fieldName="hasDiagnosis"
											fieldValue={formikBag.values.hasDiagnosis}
											onChange={() => {
												formikBag.setFieldTouched('hasDiagnosis', true);
												formikBag.values.hasDiagnosis && formikBag.setFieldValue('disease', 0);
											}}
											disabled={false}
										/>
										been&nbsp;
										<br className="sm-break" />
										diagnosed with
										<br className="sm-break" />
										<br className="xs-break" />
										<DecoratedAutoComplete
											optionList={diseaseList}
											formikBag={formikBag}
											fieldName="disease"
											fieldValue={!formikBag.values?.hasDiagnosis ? 0 : null}
											disabled={!formikBag.values.hasDiagnosis}
										/>
										.
									</span>
									<br />
									{Object.values(formikBag.errors).some((el) => el) && formikBag.submitCount > 0 && (
										<div className="submit-validate-error" style={{ color: 'red', marginBottom: '16px' }}>
											All fields should be completed
										</div>
									)}

									<button type="submit" className="btn btn-primary btn-md">
										Continue
									</button>
								</Form>
							) : formikBag.values.petType !== 'other' ? (
								<Navigate
									to={{
										pathname: `/quality-of-life-assessment/question/${0}`,
									}}
									state={{
										questions,
										sessionId,
										petType: pet.petType,
										petName: pet.name,
									}}
								/>
							) : (
								<Navigate
									to={{
										pathname: '/quality-of-life-assessment/different-species',
									}}
									state={{ name: formikBag.values.name }}
								/>
							);
						}}
					</Formik>

					<div className="pet-data-form__footer">
						<a
							href={QolScale}
							target="_blank"
							onClick={() => eventToLink('Download a printable copy of our Lap of Love Quality of Life Scale')}
							rel="noopener noreferrer"
						>
							Download a printable copy of our
							{' '}
							<b>Lap of Love Quality-of-Life Scale</b>
						</a>
					</div>
				</div>
			</div>
		</HelmetProvider>
	);
};

export default QualityOfLifeAssessment;
